import type { PlayMode, Settings, StartScreen, StartScreenDeps, WorldId } from '../types';

/** The four activities, as shown on the start screen. */
const MODES: ReadonlyArray<{ id: PlayMode; icon: string; title: string; age: string; desc: string }> = [
  { id: 'explore', icon: '🔤', title: 'Explore', age: '1+', desc: 'Letters, numbers, shapes & colours' },
  { id: 'find-letters', icon: '🔎', title: 'Find letters', age: '3+', desc: 'Find the letter on the keyboard' },
  { id: 'find-numbers', icon: '🔢', title: 'Find numbers', age: '3+', desc: 'Find the number on the keyboard' },
  { id: 'spell', icon: '✏️', title: 'Spell', age: '4+', desc: 'Build short picture words' },
];

const TITLE = 'KeySplash';
/** Matches the CSS fade duration; after it the element gets `hidden`. */
const FADE_MS = 280;
/** Ignore a second start this soon after the first (mash while fullscreen is pending). */
const RESTART_GUARD_MS = 400;

/** Keys that never start play: bare modifiers, focus navigation and Escape. */
const IGNORED_KEYS = new Set([
  'Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'OS', 'Super', 'Hyper', 'Fn', 'FnLock',
  'CapsLock', 'NumLock', 'ScrollLock', 'Symbol', 'SymbolLock', 'Tab', 'Escape',
  'ContextMenu', 'PrintScreen', 'Power', 'Eject', 'WakeUp', 'Unidentified',
]);

/** Function, media and system keys belong to grown-ups (volume, brightness, devtools…). */
function isGrownUpKey(key: string): boolean {
  return IGNORED_KEYS.has(key) || /^F\d{1,2}$/.test(key) || /^(Audio|Media|Launch|Browser|Brightness)/.test(key);
}

const ARROW_STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

const PLAY_SVG =
  '<svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">' +
  '<path d="M38 24.5c0-4.2 4.6-6.8 8.2-4.6l33 20.5c3.4 2.1 3.4 7.1 0 9.2l-33 20.5c-3.6 2.2-8.2-.4-8.2-4.6z" fill="currentColor"/>' +
  '</svg>';

const SPEAKER_BASE =
  '<path d="M4 9.5h3.2L12 5.2v13.6L7.2 14.5H4z" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>';
const VOICE_ON_SVG =
  '<svg class="ks-voice__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + SPEAKER_BASE +
  '<path d="M15.5 9a4.2 4.2 0 0 1 0 6M18.2 6.6a8 8 0 0 1 0 10.8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
  '</svg>';
const VOICE_OFF_SVG =
  '<svg class="ks-voice__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + SPEAKER_BASE +
  '<path d="M16 9.5l5 5M21 9.5l-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
  '</svg>';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

/**
 * The first screen: a calm wordmark, the activity cards, a Start button, a
 * quick world picker and a footnote telling grown-ups how to reach the controls.
 *
 * It is visible right after construction (calling show() again is harmless).
 * Any key except bare modifiers / Tab / Escape, a click on Play, or a click on
 * the empty backdrop calls `deps.onStart()` synchronously inside that event, so
 * the orchestrator can request fullscreen with the user activation.
 */
export class DomStartScreen implements StartScreen {
  private readonly el: HTMLElement;
  private readonly greeting: HTMLElement;
  private readonly play: HTMLButtonElement;
  private readonly picker: HTMLElement;
  private readonly worldButtons = new Map<WorldId, HTMLButtonElement>();
  private readonly worldName: HTMLElement;
  private readonly modeButtons = new Map<PlayMode, HTMLButtonElement>();
  private readonly modes: HTMLElement;
  private readonly secretWordEl: HTMLElement;
  private readonly voiceSwitch: HTMLButtonElement;
  private renderedVoice: boolean | null = null;
  private readonly unsubscribe: () => void;

  private visible = false;
  private hideTimer = 0;
  private lastStart = -Infinity;

  constructor(
    root: HTMLElement,
    private readonly deps: StartScreenDeps,
  ) {
    this.el = el('section', 'ks-start');
    this.el.setAttribute('aria-label', 'KeySplash — start');
    this.el.hidden = true;

    const main = el('div', 'ks-start__main');

    this.greeting = el('p', 'ks-start__greeting');
    this.greeting.hidden = true;

    const title = el('h1', 'ks-title');
    title.setAttribute('aria-label', TITLE);
    [...TITLE].forEach((char, i) => {
      const letter = el('span', 'ks-title__letter', char);
      letter.setAttribute('aria-hidden', 'true');
      letter.style.setProperty('--i', String(i));
      title.append(letter);
    });

    const subtitle = el('p', 'ks-start__subtitle', 'Every key teaches something new.');

    this.play = el('button', 'ks-play');
    this.play.type = 'button';
    this.play.setAttribute('aria-label', 'Start');
    this.play.innerHTML = PLAY_SVG;
    this.play.append(el('span', 'ks-play__label', 'Start'));

    const hint = el('p', 'ks-start__hint', 'or press any key');

    // The clear voice on/off switch. `data-no-start`: clicking it never starts play.
    this.voiceSwitch = el('button', 'ks-voice');
    this.voiceSwitch.type = 'button';
    this.voiceSwitch.setAttribute('role', 'switch');
    this.voiceSwitch.setAttribute('aria-label', 'Voice'); // state comes from aria-checked
    this.voiceSwitch.dataset.noStart = '';
    this.voiceSwitch.addEventListener('click', this.toggleVoice);
    this.voiceSwitch.addEventListener('keydown', this.onVoiceKey);
    this.voiceSwitch.addEventListener('keyup', this.onVoiceKeyUp);

    this.picker = el('div', 'ks-worlds');
    this.picker.setAttribute('role', 'radiogroup');
    this.picker.setAttribute('aria-label', 'Choose a world');
    this.picker.dataset.noStart = '';
    for (const world of deps.worlds) {
      const button = el('button', 'ks-world');
      button.type = 'button';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-label', world.label);
      button.title = world.label;
      button.dataset.world = world.id;
      const icon = el('span', 'ks-world__icon', world.icon);
      icon.setAttribute('aria-hidden', 'true');
      button.append(icon);
      this.picker.append(button);
      this.worldButtons.set(world.id, button);
    }
    this.picker.addEventListener('click', this.onPickerClick);
    this.picker.addEventListener('keydown', this.onPickerKey);
    this.worldName = el('p', 'ks-start__world');
    this.worldName.dataset.noStart = '';
    this.worldName.setAttribute('aria-hidden', 'true'); // the radios already announce it

    const modes = (this.modes = el('div', 'ks-modes'));
    modes.setAttribute('role', 'radiogroup');
    modes.setAttribute('aria-label', 'Choose an activity');
    modes.dataset.noStart = '';
    for (const mode of MODES) {
      const card = el('button', 'ks-mode');
      card.type = 'button';
      card.setAttribute('role', 'radio');
      card.dataset.mode = mode.id;
      const icon = el('span', 'ks-mode__icon', mode.icon);
      icon.setAttribute('aria-hidden', 'true');
      const head = el('span', 'ks-mode__head');
      head.append(el('span', 'ks-mode__title', mode.title), el('span', 'ks-mode__age', mode.age));
      card.append(icon, head, el('span', 'ks-mode__desc', mode.desc));
      card.addEventListener('click', () => {
        if (this.deps.store.get().mode !== mode.id) this.deps.store.update({ mode: mode.id });
      });
      modes.append(card);
      this.modeButtons.set(mode.id, card);
    }
    modes.addEventListener('keydown', this.onModesKey);

    main.append(this.greeting, title, subtitle, modes, this.play, hint, this.voiceSwitch, this.picker, this.worldName);

    // Grown-up footnote.
    const foot = el('footer', 'ks-start__foot');
    foot.dataset.noStart = '';
    const how = el('p', 'ks-start__how');
    this.secretWordEl = el('b', 'ks-start__secret');
    how.append('Grown-ups: while playing, type ', this.secretWordEl, ' or hold the top-left corner to open the controls.');
    const tips = el('ul', 'ks-tips');
    tips.append(
      el('li', undefined, 'Works offline. Nothing leaves this device.'),
      el('li', undefined, 'Chrome and Edge lock Escape and most shortcuts in fullscreen.'),
      el('li', undefined, 'On a Mac, turn on Chrome ▸ “Warn Before Quitting”.'),
    );
    foot.append(how);
    if (deps.onOpenControls) {
      // A small, low-contrast link for grown-ups (inside the footnote, so it never starts play).
      const controls = el('button', 'ks-start__controls', '⚙️ Grown-up settings');
      controls.type = 'button';
      controls.addEventListener('click', () => deps.onOpenControls?.());
      foot.append(controls);
    }
    foot.append(tips);

    this.el.append(main, foot);
    this.el.addEventListener('click', this.onClick);
    root.append(this.el);

    this.render(deps.store.get());
    this.unsubscribe = deps.store.subscribe((next) => this.render(next));
    this.show();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    window.clearTimeout(this.hideTimer);
    this.el.hidden = false;
    void this.el.offsetWidth; // commit `display` so the fade-in transition runs
    this.el.classList.add('is-shown');
    window.addEventListener('keydown', this.onKeyDown);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    window.removeEventListener('keydown', this.onKeyDown);
    this.el.classList.remove('is-shown');
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.el.hidden = true;
    }, FADE_MS);
  }

  /** Removes the element and every listener (tests / hot reload). */
  destroy(): void {
    this.hide();
    window.clearTimeout(this.hideTimer);
    this.unsubscribe();
    this.el.remove();
  }

  // ---------------------------------------------------------------------------

  private render(settings: Settings): void {
    const name = settings.childName.trim();
    this.greeting.hidden = name === '';
    this.greeting.textContent = name ? `Hi, ${name}!` : '';
    this.secretWordEl.textContent = settings.secretWord;
    this.worldName.textContent = this.deps.worlds.find((w) => w.id === settings.world)?.label ?? '';
    this.renderVoice(settings.voice);
    for (const [id, card] of this.modeButtons) {
      const selected = id === settings.mode;
      card.setAttribute('aria-checked', String(selected));
      card.tabIndex = selected ? 0 : -1;
      card.classList.toggle('is-selected', selected);
    }
    for (const [id, button] of this.worldButtons) {
      const selected = id === settings.world;
      button.setAttribute('aria-checked', String(selected));
      button.tabIndex = selected ? 0 : -1;
      button.classList.toggle('is-selected', selected);
    }
    // Lets the CSS honour the parent's motion choice on top of the OS setting
    // (see the reduced-motion rules at the end of styles.css).
    document.documentElement.dataset.motion = settings.motion;
  }

  private renderVoice(on: boolean): void {
    if (this.renderedVoice === on) return;
    this.renderedVoice = on;
    const sw = this.voiceSwitch;
    sw.setAttribute('aria-checked', String(on));
    sw.classList.toggle('is-on', on);
    sw.innerHTML = on ? VOICE_ON_SVG : VOICE_OFF_SVG;
    const track = el('span', 'ks-voice__track');
    track.setAttribute('aria-hidden', 'true');
    sw.append(el('span', 'ks-voice__label', on ? 'Voice on' : 'Voice off'), track);
  }

  private readonly toggleVoice = (): void => {
    this.deps.store.update({ voice: !this.deps.store.get().voice });
  };

  /**
   * Enter/Space toggle on keydown. Default is prevented (and Space's keyup too)
   * so the browser's native button activation can't toggle a second time.
   */
  private readonly onVoiceKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat) this.toggleVoice();
  };

  private readonly onVoiceKeyUp = (event: KeyboardEvent): void => {
    if (event.key === ' ') event.preventDefault();
  };

  private start(): void {
    if (!this.visible) return;
    const now = performance.now();
    if (now - this.lastStart < RESTART_GUARD_MS) return;
    this.lastStart = now;
    this.deps.onStart();
  }

  private readonly onClick = (event: MouseEvent): void => {
    // Use the path captured at dispatch: a control may re-render while handling the
    // click (the voice switch swaps its icon/label), detaching the original target,
    // and a detached node no longer finds its [data-no-start] ancestor.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.some((node) => node instanceof Element && node.hasAttribute('data-no-start'))) return;
    const target = event.target;
    if (target instanceof Element && (target.closest('[data-no-start]') || !target.isConnected)) return;
    this.start();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || event.isComposing || isGrownUpKey(event.key)) return;
    // Shortcut chords (Cmd+R, Ctrl+Shift+I…) belong to the grown-up.
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target;
    if (target instanceof Element && target.closest('[data-allow-keys]')) return;
    if (isEditable(target)) return;
    if (target instanceof Element && this.el.contains(target)) {
      // Let a focused button (Play, a world) activate natively instead.
      if ((event.key === 'Enter' || event.key === ' ') && target.closest('button')) return;
      // Arrow keys move through the world picker and the activity cards.
      if (event.key in ARROW_STEP && (this.picker.contains(target) || this.modes.contains(target))) return;
    }
    event.preventDefault();
    this.start();
  };

  private readonly onPickerClick = (event: MouseEvent): void => {
    const target = event.target;
    const button = target instanceof Element ? target.closest<HTMLButtonElement>('.ks-world') : null;
    const id = button?.dataset.world as WorldId | undefined;
    if (id && id !== this.deps.store.get().world) this.deps.store.update({ world: id });
  };

  /** Activity cards: roving radio-group navigation (arrows move and select). */
  private readonly onModesKey = (event: KeyboardEvent): void => {
    const step = ARROW_STEP[event.key];
    if (!step) return;
    event.preventDefault();
    const ids = [...this.modeButtons.keys()];
    const current = ids.indexOf(this.deps.store.get().mode);
    const next = ids[(Math.max(0, current) + step + ids.length) % ids.length];
    this.deps.store.update({ mode: next });
    this.modeButtons.get(next)?.focus();
  };

  /** Roving radio-group navigation: arrows move and select. */
  private readonly onPickerKey = (event: KeyboardEvent): void => {
    const step = ARROW_STEP[event.key];
    if (!step) return;
    event.preventDefault();
    const ids = [...this.worldButtons.keys()];
    if (ids.length === 0) return;
    const current = ids.indexOf(this.deps.store.get().world);
    const next = ids[(Math.max(0, current) + step + ids.length) % ids.length];
    this.deps.store.update({ world: next });
    this.worldButtons.get(next)?.focus();
  };
}
